import {cva} from 'platform/shared';
import {match, Pattern} from 'ts-pattern';

import {CSSProperties, ForwardedRef, forwardRef, ReactNode, UIEventHandler} from 'react';

import {always} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {CSSDimension} from '../../types/CSSDimension';
import {Integer} from '../../types/Integer';
import {getCssSize} from '../../utils/getCssSize';

const scrollVariants = cva('', {
  variants: {
    overflow: {
      auto: 'overflow-auto',
      scroll: 'overflow-scroll',
      hidden: 'overflow-hidden',
      initial: '[overflow:initial]',
    },
    hasHiddenScrollBars: {
      true: '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
      false: '',
    },
    showGutter: {
      true: '[scrollbar-gutter:stable]',
      false: '',
    },
  },
});

export interface ScrollProps extends TestIdProps {
  auto?: true;
  always?: true;
  never?: true;
  children?: ReactNode;
  width?: CSSDimension | Integer;
  height?: CSSDimension | Integer;
  maxHeight?: CSSDimension | Integer;
  maxWidth?: CSSDimension | Integer;
  minHeight?: CSSDimension | Integer;
  minWidth?: CSSDimension | Integer;
  flex?: number;
  hasHiddenScrollBars?: true;
  onScroll?: UIEventHandler<HTMLDivElement>;
  showGutter?: boolean;
}

export const Scroll = forwardRef((props: ScrollProps, ref: ForwardedRef<HTMLDivElement>) => {
  const overflow = match([props.auto, props.always, props.never])
    .with([true, Pattern.any, Pattern.any], always('auto' as const))
    .with([Pattern.any, true, Pattern.any], always('scroll' as const))
    .with([Pattern.any, Pattern.any, true], always('hidden' as const))
    .otherwise(always('initial' as const));

  const style: CSSProperties = {
    width: getCssSize(props.width),
    height: getCssSize(props.height),
    maxHeight: getCssSize(props.maxHeight),
    minHeight: getCssSize(props.minHeight),
    maxWidth: getCssSize(props.maxWidth),
    minWidth: getCssSize(props.minWidth),
    flex: props.flex ?? 'initial',
  };

  return (
    <div
      ref={ref}
      onScroll={props.onScroll}
      className={scrollVariants({
        overflow,
        hasHiddenScrollBars: Boolean(props.hasHiddenScrollBars),
        showGutter: Boolean(props.showGutter),
      })}
      style={style}
      data-testid={suffixTestId('scroll', props)}
    >
      {props.children}
    </div>
  );
});
